import { defineComponent as _defineComponent } from 'vue'
import {computed, onMounted, onUnmounted, ref, watch} from 'vue';
import useClickOutside from '@/composables/useClickOutside';
import PopupContainer from "@/components/popup.vue";
import AddParentBuilder from "./add-parent-builder.vue";
import toolTip from '@/components/tool-tip.vue';

import SaveBuilderWarning from '@/components/saveBuilderWarning.vue';
import BuilderAlreadyExists from "@/components/builderExistsPopup.vue";

import { type DataInstance, Builders } from '@busy-human/hxp-library';

import { useShoppingBuilderStore } from '@/store/builder-shopping';
import { useAuth } from '@/store/auth';
import { useZohoStrings } from "@/store/zohoStrings";
import { useToast } from 'vue-toastification';
import { useFilterStore } from '@/store/filter';

import emitter, { EventTypes } from '@/events';
import { useSeasonBuilders } from '@/store/builder-seasons';

enum addState {
	CREATE_TEEN = 0,
	CREATE_PARENT = 1,
}

type Props = {
	addToParent?: string
}
type Emits = {
	(e: 'close'): void
}

export default /*#__PURE__*/_defineComponent({
  __name: 'add-builder',
  props: {
    addToParent: {}
  },
  emits: ["close"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

const auth = useAuth();
const builderStore = useShoppingBuilderStore();
const builderSeasons = useSeasonBuilders();
const zohoStrings = useZohoStrings();
const toast = useToast();
const showBuilderSaveWarning = ref(false);
const showBuilderExists = ref(false);

const state = ref(addState.CREATE_TEEN);

const props = __props;

const emit = __emit;

const whatIsAParentTitleHtml = computed(() => zohoStrings.strings?.zh_parentBuilderNoteTitle?.rendered || 'What is a Parent Builder?');
const whatIsAParentSubTitleHtml = computed(() => zohoStrings.strings?.zh_parentBuilderNoteSubtitle?.rendered || 'And why do teens with a Parent Builder get their preferred trips?');
const whatIsAParentBodyHtml = computed(() => zohoStrings.strings?.zh_parentBuilderNoteDescription?.rendered || 'Parent');
const teenToOldTitle = computed(() => zohoStrings.strings?.zh_teenTooOldTitle?.rendered || "Hey there");
const teenToOldDetail = computed(() => zohoStrings.strings?.zh_teenTooOldDetail?.rendered || "You're too old to be allowed to go as a Teen Builder for trips this season");


const workingTeenBuilder = ref<Partial<Builders.Model>>({...Builders.Default(), gender: undefined});

const teenBuilderBirthday = computed({
	get() {
		if(!workingTeenBuilder.value?.birthday) return undefined;
		return workingTeenBuilder.value.birthday;
	},
	set(val) {
		if(!val) {delete workingTeenBuilder.value?.birthday;}
		else {
			const tmp = new Date(val);
			if(tmp instanceof Date && !isNaN(tmp.valueOf())) {
				workingTeenBuilder.value.birthday = Builders.dateToBirthdate(tmp);
			}
		}
	}
});

const bringingParent = ref<'Yes' | 'Maybe'| 'No' | null>(null);

const teenBirthdayValid = computed<'valid' | 'tooYoung' | 'tooOld' | 'invalid'>(() => builderStore.validateTeenBirthday(teenBuilderBirthday.value));


const resolvedBringingParent = computed<'Yes' | 'Maybe'| 'No'>(() => {
	if(props.addToParent) return "No"; // Shortcut lol
	if(!bringingParent.value) return 'No';
	if(teenBirthdayValid.value === 'valid' || teenBirthdayValid.value === 'invalid'){
		return bringingParent.value;
	}else{
		return 'No';
	}
});



async function saveBuilders(parentBuilder: Partial<Builders.Model> | null, startNewTeen: boolean){

	const teenId = builderStore.addBuilder();
	if(workingTeenBuilder.value.name){
		workingTeenBuilder.value.name.fullName = `${workingTeenBuilder.value.name?.firstName} ${workingTeenBuilder.value.name?.lastName}`;
	}
	Object.assign(builderStore.builders[teenId], workingTeenBuilder.value);


	if(parentBuilder){
		const parentId = builderStore.addBuilder();
		Object.assign(builderStore.builders[parentId], parentBuilder);

		if(parentBuilder.name){
			builderStore.builders[parentId].name.fullName = `${parentBuilder.name.firstName} ${parentBuilder.name.lastName}`;
		}

		//Link the teen/parent
		builderStore.setBuilderPair(teenId, parentId);

		//Save the parent
		if(auth.isAuthenticated) await builderStore.saveBuilder(parentId);
	}

	builderStore.setMaybeParent(teenId, resolvedBringingParent.value === 'Maybe');

	if(auth.isAuthenticated) {
		await builderStore.saveBuilder(teenId);
		if(props.addToParent) await builderStore.addBuilderToCurrentSeason(props.addToParent, teenId);
	}

	if(builderStore.validateTeenBirthday(teenBuilderBirthday.value) === 'tooYoung'){
		toast(`Future Builder ${builderStore.builders[teenId].name.firstName} Created!`);
	}else{
		builderStore.selectBuilder(teenId);
	}

	if(!parentBuilder && startNewTeen){
		state.value = addState.CREATE_TEEN;
	}else{
		emit('close');
	}

	if(props.addToParent) {
		emitter.emit(EventTypes.openWelcomeBack, "Add Builders from Prior Year");
	}
}

function checkIfBuilderExists(){
	let builderExists = false;
	let builderHasSameName = false;
	builderStore.selectableGroups.forEach(group =>{
		const teenBuilderId = group.teen;
		if(!teenBuilderId) throw new Error("Group does not have teen builder");
		const teenBuilder = builderStore.builders[teenBuilderId];
		if(workingTeenBuilder.value.name && workingTeenBuilder.value.birthday){
			if(workingTeenBuilder.value.name.firstName === teenBuilder.name.firstName && workingTeenBuilder.value.name.lastName === teenBuilder.name.lastName && workingTeenBuilder.value.birthday === teenBuilder.birthday){
				builderExists = true;
			}else if(teenBuilder.name.firstName === workingTeenBuilder.value.name.firstName){
				builderHasSameName = true;
			}

		}
	});
	if(builderExists){
		return "builderExists";
	}else if(builderHasSameName){
		return "builderHasSameName";
	}else{
		return "valid";
	}
}

function switchToParent() {
	const builderExists = checkIfBuilderExists();
	if(builderExists === 'builderExists'){
		showBuilderExists.value = true;
	}else if(builderExists === 'builderHasSameName'){
		showBuilderSaveWarning.value = true;
	}else{
		state.value = addState.CREATE_PARENT;
	}
}

async function saveBuilder(){
	const builderExists = checkIfBuilderExists();
	if(builderExists === 'builderExists'){
		showBuilderExists.value = true;
	}else if(builderExists === 'builderHasSameName'){
		showBuilderSaveWarning.value = true;
	}else{
		await saveBuilders(null, false);
	}
}

async function saveBuilderConfirmed(){
	console.log("HIT?!?!?!?!?!?!??!?!?!?!?!?!??!?!");
	showBuilderSaveWarning.value = false;
	await saveBuilders(null, false);
}


async function saveBuilderDuo(parentBuilder: Partial<Builders.Model>){
	await saveBuilders(parentBuilder, false);
}

async function saveLastBuilder(){
	await saveBuilders(null, true);
}

const isBoxScrollable = ref(false);
const builderDetailsElement = ref<HTMLElement | null>(null);
function checkDetailsScrollable() {
	if(!builderDetailsElement.value) {
		isBoxScrollable.value = false;
		return;
	}

	const el = builderDetailsElement.value;
	const canScroll = el.scrollHeight > el.clientHeight;
	const CLOSEDIST = 5;
	const scrolledDown = el.scrollTop > ((el.scrollHeight - el.offsetHeight) - CLOSEDIST);
	isBoxScrollable.value = (canScroll && !scrolledDown);
}
onMounted(() => {
	checkDetailsScrollable();
	window.addEventListener('resize', checkDetailsScrollable);
});
onUnmounted(() => {
	window.removeEventListener('resize', checkDetailsScrollable);
});



const __returned__ = { auth, builderStore, builderSeasons, zohoStrings, toast, showBuilderSaveWarning, showBuilderExists, addState, state, props, emit, whatIsAParentTitleHtml, whatIsAParentSubTitleHtml, whatIsAParentBodyHtml, teenToOldTitle, teenToOldDetail, workingTeenBuilder, teenBuilderBirthday, bringingParent, teenBirthdayValid, resolvedBringingParent, saveBuilders, checkIfBuilderExists, switchToParent, saveBuilder, saveBuilderConfirmed, saveBuilderDuo, saveLastBuilder, isBoxScrollable, builderDetailsElement, checkDetailsScrollable, computed, onMounted, onUnmounted, ref, watch, get useClickOutside() { return useClickOutside }, PopupContainer, AddParentBuilder, toolTip, SaveBuilderWarning, BuilderAlreadyExists, get Builders() { return Builders }, get useShoppingBuilderStore() { return useShoppingBuilderStore }, get useAuth() { return useAuth }, get useZohoStrings() { return useZohoStrings }, get useToast() { return useToast }, get useFilterStore() { return useFilterStore }, get emitter() { return emitter }, get EventTypes() { return EventTypes }, get useSeasonBuilders() { return useSeasonBuilders } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})